import React from "react";

import type { EventPropGetter } from ".";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { differenceInMinutes, getMinutes, isAfter } from "date-fns";

import { CalendarFilterContext, type Event } from "~/calendar/CalendarPage";
import EventContextMenu from "~/calendar/EventContextMenu";
import { hexToRGB } from "~/lib/utils";
import { RecordIconSwitchEvent } from "~/meetingRecorder/RecordIcon";
import { timeBlockTypeIcons } from "~/nav/panels/Meetings/utils";

import { DnDContext } from "./addons/dragAndDrop/DnDContext";
import EventIcons, { getEventIcons } from "./EventIcons";
import { isLightColor } from "./utils/colors";

import styles from "./DayColumnEvent.module.css";

function stringifyPercent(v: string | number) {
	return typeof v === "string" ? v : `${v}%`;
}

interface DayColumnEventProps {
	event: Event & { __isPreview?: boolean }; // injected in dnd WeekWrapper
	style: {
		top: number;
		height: number;
		width: number;
		xOffset: number;
		isBigContainer?: boolean;
	};
	selected: boolean;
	continuesPrior: boolean;
	continuesAfter: boolean;
	getters: { eventProp: EventPropGetter<Event> };
	onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onDoubleClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	isBackgroundEvent: boolean;
}

export default function DayColumnEvent({
	event,
	style,
	selected,
	continuesPrior,
	continuesAfter,
	getters,
	onClick,
	onDoubleClick,
	isBackgroundEvent,
}: DayColumnEventProps) {
	const { draggable } = React.useContext(DnDContext);
	const { showTileIcons } = React.useContext(CalendarFilterContext);

	let userProps = getters.eventProp(event, event.start, event.end, selected);
	const { height, top, width: _width, xOffset, isBigContainer } = style;
	const { calendarColor, fellowColor, providerEventColor } = event;
	const width = isBigContainer ? 100 : _width;

	const { colorStyles, requiresDarkText }: { colorStyles: React.CSSProperties; requiresDarkText: boolean } =
		React.useMemo(() => {
			const colorStyles: React.CSSProperties = {};
			let requiresDarkText = false;

			if (calendarColor) colorStyles["--calendarColor"] = calendarColor;

			const setToCalendarColor = () => {
				if (!calendarColor) return; // Fallback to css defaults

				requiresDarkText = isLightColor(calendarColor);
				colorStyles["--tileColor-rgb"] = hexToRGB(calendarColor);
				colorStyles["--tileColor"] = calendarColor;
			};

			const setToProviderColor = () => {
				if (!providerEventColor) return setToCalendarColor();

				requiresDarkText = isLightColor(providerEventColor);
				colorStyles["--tileColor-rgb"] = hexToRGB(providerEventColor);
				colorStyles["--tileColor"] = providerEventColor;
			};

			const setToFellowColor = () => {
				requiresDarkText = false;
				colorStyles["--tileColor-rgb"] = `var(--event-${fellowColor.toLowerCase()}-rgb)`;
				colorStyles["--tileColor"] = `var(--event-${fellowColor.toLowerCase()})`;
			};

			switch (fellowColor) {
				case "CALENDAR": {
					setToCalendarColor();
					break;
				}
				case "PROVIDER": {
					setToProviderColor();
					break;
				}
				default: {
					setToFellowColor();
					break;
				}
			}

			return {
				colorStyles,
				requiresDarkText,
			};
		}, [calendarColor, fellowColor, providerEventColor]);

	// When events overlap, their width is condensed to make room for other events.
	// On hover, we want to grow the event horizontally, but not so much that it covers the other events.
	// This percentage of the day column represents how big the event should grow.
	const MIN_WIDTH_ON_HOVER = 80;

	const eventStyle = {
		...userProps.style,
		...colorStyles,
		top: stringifyPercent(top),
		width: stringifyPercent(width),
		height: stringifyPercent(height),

		// Just a placeholder
		"--hoverWidth": stringifyPercent(width),
	};

	// We don't want the event to expand past the bounds of the column.
	// This uses the position corresponding to which half of the column the event lies (based on the middle of the event),
	// that way expansion goes in the opposite direction.
	// i.e. Use `left` when the event is on the left half
	if (xOffset <= 50 - width / 2) {
		eventStyle.left = stringifyPercent(xOffset);
		eventStyle["--hoverWidth"] = stringifyPercent(Math.max(width, Math.min(MIN_WIDTH_ON_HOVER, 100 - xOffset)));
	} else {
		eventStyle.right = stringifyPercent(100 - xOffset - width);
		eventStyle["--hoverWidth"] = stringifyPercent(Math.max(width, Math.min(MIN_WIDTH_ON_HOVER, xOffset + width)));
	}

	if (isBackgroundEvent) {
		// Note: background events haven't been adapted to all of our changes so far
		// This probably doens't work as intended
		// Adding 10px to take events container right margin into account
		eventStyle.width = `calc(${width} + 10px)`;
		eventStyle.left = stringifyPercent(Math.max(0, xOffset));
	}

	const { timeLabel, eventDuration, isPastEvent } = React.useMemo(() => {
		const eventDuration = differenceInMinutes(event.end, event.start);

		const startTimeFormatOptions: Intl.DateTimeFormatOptions = {
			hour: "numeric",
			hour12: true,
		};

		if (getMinutes(event.start) !== 0) startTimeFormatOptions.minute = "numeric";

		const isPastEvent = isAfter(new Date(), event.end);

		return {
			eventDuration,
			timeLabel: new Intl.DateTimeFormat("en-US", startTimeFormatOptions).format(event.start),
			isPastEvent,
		};
	}, [event.start, event.end]);

	const { draggableCondition, resizableCondition, selectableCondition } = draggable;

	const isDraggable = React.useMemo(() => draggableCondition(event), [event, draggableCondition]);
	const isResizable = React.useMemo(() => resizableCondition(event), [event, resizableCondition]);

	// TODO: The `selected` prop can still make this event selected
	const isSelectable = React.useMemo(() => selectableCondition?.(event) ?? true, [event, selectableCondition]);

	const handleStartDragging = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (e.button !== 0 || !isDraggable) return;
			draggable.onBeginAction(event, "move");
		},
		[draggable, event, isDraggable],
	);

	const handleStartResizing = React.useCallback(
		(direction: "up" | "down") => (e: React.MouseEvent<HTMLSpanElement>) => {
			if (e.button !== 0 || !isResizable) return;

			switch (direction) {
				case "up": {
					return draggable.onBeginAction(event, "resize", "UP");
				}
				case "down": {
					return draggable.onBeginAction(event, "resize", "DOWN");
				}
			}
		},
		[draggable, event, isResizable],
	);

	const isNeedsAction = ["needsAction", "none", "notResponded"].includes(event.userResponseStatus || "");
	const isDifferentColors = colorStyles["--tileColor"] !== colorStyles["--calendarColor"];
	const isTimeBlock = event.timeBlockType === "focusTime" || event.timeBlockType === "outOfOffice";
	const eventIcons = getEventIcons(event, showTileIcons);

	const onClickRecordIconSwitch = React.useCallback((e: React.MouseEvent) => e.stopPropagation(), []);
	const recordIconEvent = { ...event, start: event.start.toISOString() };

	return (
		<EventContextMenu disabled={event.blockType !== "real" || !isSelectable} event={event}>
			<div
				id={`event-${event.guid}`}
				onClick={onClick}
				onDoubleClick={onDoubleClick}
				style={eventStyle}
				data-event-type={event.blockType}
				className={cx(
					styles.event,
					isBackgroundEvent ? "fbc-background-event" : "fbc-event",
					eventDuration < 30 ? styles.tinyEvent : eventDuration < 45 ? styles.smallEvent : {},
					styles[event.attendeePersonCount === 0 || !event.userResponseStatus ? "" : event.userResponseStatus],
					{
						[styles.notSelectable]: !isSelectable,
						[styles.selected]: selected,
						[styles.continuesEarlier]: continuesPrior,
						[styles.continuesAfter]: continuesAfter,
						[styles.isOverlapping]: xOffset > 0,
						[styles.pastEvent]: isPastEvent,
						[styles.filteredOut]: event.filteredOut,
						[styles.cancelled]: event.status === "cancelled",
						[styles.isParticipant]: event.blockType === "participant",
						[styles.imaginaryEvent]: event.blockType === "imaginary",
						[styles.loadingEvent]: event.blockType === "loading",
						[styles.isLightColor]: requiresDarkText,
						[styles.narrow]: width < 40,
						[styles.isBigContainer]: isBigContainer,
						[styles.withBar]: isNeedsAction && isDifferentColors,
						["fbc-addons-dnd-dragged-event"]:
							draggable.dragAndDropAction?.interacting && draggable.dragAndDropAction.event === event, // if an event is being dragged right now // and it's the current event
						// eslint-disable-next-line no-underscore-dangle
						["fbc-addons-dnd-drag-preview"]: event.__isPreview,
						[styles.willBeRecorded]: event.blockType === "real" && event.willBeRecorded === "YES",
						[styles.unknownRecordingStatus]: event.blockType === "real" && event.willBeRecorded === "UNKNOWN",
					},
				)}
			>
				{!continuesPrior && isResizable && (
					<span className={cx(styles.resizeGrip, styles.first)} onMouseDown={handleStartResizing("up")} />
				)}
				<div
					className={cx(styles.eventContent, {
						[styles.isTimeBlock]: isTimeBlock,
					})}
					onMouseDown={handleStartDragging}
					title={event.title}
				>
					<div className={styles.calendarBar} />
					<div className={styles.eventTitleContainer}>
						{isTimeBlock && <FontAwesomeIcon fixedWidth icon={timeBlockTypeIcons[event.timeBlockType]} />}
						<div className={styles.eventTitle}>{event.title}</div>
						{event.blockType === "real" && (
							<RecordIconSwitchEvent
								event={recordIconEvent}
								className={styles.recordIconSwitch}
								onClick={onClickRecordIconSwitch}
							/>
						)}
					</div>
					<div className={styles.eventTime}>
						{event.status === "cancelled" ? "Cancelled" : !isTimeBlock ? timeLabel : null}
					</div>
				</div>
				{!continuesAfter && isResizable && (
					<span
						className={cx(styles.resizeGrip, styles.last)}
						onMouseDown={handleStartResizing("down")}
						style={eventIcons.length > 0 ? { width: `calc(100% - ${eventIcons.length * 10 + 2}px)` } : {}}
					/>
				)}
				<div className={styles.iconsWrapper}>
					<EventIcons icons={eventIcons} />
				</div>
			</div>
		</EventContextMenu>
	);
}
